import axios from 'axios';

// const api = axios.create({ baseURL: 'https://wasocket.grepixit.com/api' });
const api = axios.create({ baseURL: '/api' });

// Attach JWT token to every request
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('wa_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-logout on 401
api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('wa_token');
      window.location.href = '/';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const login        = (email, password) => api.post('/auth/login', { email, password });
export const getMe        = ()                 => api.get('/auth/me');
export const getUsers     = ()                 => api.get('/auth/users');
export const registerUser = (data)             => api.post('/auth/register', data);
export const updateUser   = (id, data)         => api.patch(`/auth/users/${id}`, data);
export const deleteUser   = (id)               => api.delete(`/auth/users/${id}`);
export const getRolePermissions    = ()         => api.get('/auth/role-permissions');
export const updateRolePermission  = (role, d)  => api.patch(`/auth/role-permissions/${role}`, d);

// ── Accounts ──────────────────────────────────────────────────
export const getAccounts      = ()       => api.get('/accounts');
export const addAccount       = (data)   => api.post('/accounts', data);
export const connectAccount   = (id)     => api.post(`/accounts/${id}/connect`);
export const getQR            = (id)     => api.get(`/accounts/${id}/qr`);
export const disconnectAccount= (id)     => api.delete(`/accounts/${id}/disconnect`);
export const updateAccount    = (id, d)  => api.patch(`/accounts/${id}`, d);
export const deleteAccount    = (id)     => api.delete(`/accounts/${id}`);
export const resetDaily       = ()       => api.post('/accounts/reset-daily');
export const setPrimaryAccount= (id)     => api.post(`/accounts/${id}/set-primary`);
export const initiateChat     = (data)   => api.post('/messages/initiate-chat', data);

// ── Campaigns ─────────────────────────────────────────────────
export const getCampaigns     = ()          => api.get('/campaigns');
export const getCampaign      = (id)        => api.get(`/campaigns/${id}`);
export const createCampaign   = (data)      => api.post('/campaigns', data);
export const updateCampaign   = (id, data)  => api.put(`/campaigns/${id}`, data);
export const deleteCampaign   = (id)        => api.delete(`/campaigns/${id}`);
export const setCampaignStatus= (id, status)=> api.patch(`/campaigns/${id}/status`, { status });
export const enrollLeads      = (id, lead_ids) => api.post(`/campaigns/${id}/enroll`, { lead_ids });
export const sendNow          = (id)        => api.post(`/campaigns/${id}/send-now`);
export const getCampaignStats     = (id)    => api.get(`/campaigns/${id}/stats`);
export const getCampaignSequences = (id)    => api.get(`/campaigns/${id}/sequences`);
export const duplicateCampaign    = (id)    => api.post(`/campaigns/${id}/duplicate`);

// ── Leads ─────────────────────────────────────────────────────
export const getLeads      = (params) => api.get('/leads', { params });
export const getPipeline   = ()       => api.get('/leads/pipeline');
export const getLeadStats  = ()       => api.get('/leads/stats');
export const getLeadSources= ()       => api.get('/leads/sources');
export const getLeadTags      = ()    => api.get('/leads/tags');
export const getLeadStaff     = ()    => api.get('/leads/staff');
export const getLeadCountries = ()    => api.get('/leads/countries');
export const getLead       = (id)     => api.get(`/leads/${id}`);
export const createLead    = (data)   => api.post('/leads', data);
export const updateLead    = (id, d)  => api.patch(`/leads/${id}`, d);
export const deleteLead    = (id)     => api.delete(`/leads/${id}`);
export const bulkImport    = (leads, campaign_id) => api.post('/leads/bulk-import', { leads, campaign_id });

// ── Messages ──────────────────────────────────────────────────
export const getInbox      = (params) => api.get('/messages/inbox', { params });
export const getMessages   = (leadId, accountId) => api.get(`/messages/${leadId}`, { params: accountId ? { account_id: accountId } : {} });
export const sendMessage   = (data)   => api.post('/messages/send', data);
export const getQueueStats = ()       => api.get('/messages/queue/stats');
export const syncChats    = (accountId) => api.post(`/messages/sync/${accountId}`);

// ── Analytics ─────────────────────────────────────────────────
const userScoped = userId => ({ params: userId ? { user_id: userId } : {} });
export const getDashboard  = (userId)  => api.get('/analytics/dashboard', userScoped(userId));
export const getWeekly     = (userId)  => api.get('/analytics/weekly', userScoped(userId));
export const getCampaignAnalytics = (userId) => api.get('/analytics/campaigns', userScoped(userId));
export const getAccountAnalytics  = (userId) => api.get('/analytics/accounts', userScoped(userId));

// ── Templates ─────────────────────────────────────────────────
export const getTemplates         = ()        => api.get('/analytics/templates');
export const createTemplate       = (data)    => api.post('/analytics/templates', data);
export const updateTemplate       = (id, data)=> api.put(`/analytics/templates/${id}`, data);
export const deleteTemplate       = (id)      => api.delete(`/analytics/templates/${id}`);
export const generateVariations   = (data)    => api.post('/analytics/templates/generate-variations', data);

// ── Auto-replies ──────────────────────────────────────────────
export const getAutoReplies   = ()          => api.get('/analytics/auto-replies');
export const createAutoReply  = (data)      => api.post('/analytics/auto-replies', data);
export const updateAutoReply  = (id, data)  => api.patch(`/analytics/auto-replies/${id}`, data);
export const deleteAutoReply  = (id)        => api.delete(`/analytics/auto-replies/${id}`);

// ── Integrations ──────────────────────────────────────────────
export const getIntegrations   = ()             => api.get('/analytics/integrations');
export const updateIntegration = (name, data)   => api.patch(`/analytics/integrations/${name}`, data);

// ── AI Reply (Anthropic direct) ───────────────────────────────
export const generateAIReply = async (leadName, campaign, stage, lastMessage) => {
  const r = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1000,
      messages: [{
        role: 'user',
        content: `You are a professional sales assistant for Grepix Infotech, an Indian tech company selling white-label apps: HireMe (taxi), MasalaDish (food delivery), GTA Logistics (trucking), InstaResQ (roadside assistance), BidnRide (ride bidding).
Lead: ${leadName}, Campaign: ${campaign}, Stage: ${stage}.
Their last message: "${lastMessage}".
Write a warm, professional WhatsApp reply (2-3 sentences). Include a soft call-to-action. No markdown.`
      }]
    })
  });
  const d = await r.json();
  return d.content?.[0]?.text || '';
};

export default api;
